import { useState, useEffect } from "react"
import { UserPlus } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { api } from "@/lib/api-client"
import type { UserRef } from "@/lib/types"

interface BulkAssignDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  count: number
  onConfirm: (userId: string) => Promise<void>
  fieldLabel?: string
}

export function BulkAssignDialog({
  open,
  onOpenChange,
  count,
  onConfirm,
  fieldLabel = "Opiekun",
}: BulkAssignDialogProps) {
  const [users, setUsers] = useState<UserRef[]>([])
  const [selectedUserId, setSelectedUserId] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (open) {
      // Fetch users for assignment. We'll use auth/me endpoint pattern
      // or a search endpoint. The app has users available through various means.
      // Let's fetch from a lightweight endpoint.
      api
        .get<{ data: { id: string; first_name: string; last_name: string; email: string }[] }>(
          "/api/v1/search?q=&type=users&limit=50"
        )
        .then((res) => {
          if (res.data) {
            setUsers(
              res.data.map((u) => ({
                id: u.id,
                first_name: u.first_name,
                last_name: u.last_name,
              }))
            )
          }
        })
        .catch(() => {
          // Fallback: if the search endpoint doesn't support users, use empty list
          setUsers([])
        })
    }
  }, [open])

  const handleConfirm = async () => {
    if (!selectedUserId) return
    setIsSubmitting(true)
    try {
      await onConfirm(selectedUserId)
      onOpenChange(false)
      setSelectedUserId("")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus size={20} className="text-primary" />
            {fieldLabel} - masowa zmiana
          </DialogTitle>
          <DialogDescription>
            Zmien {fieldLabel.toLowerCase()} dla {count}{" "}
            {count === 1 ? "elementu" : "elementow"}.
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <label className="text-sm font-medium text-foreground mb-2 block">
            Wybierz uzytkownika
          </label>
          <select
            value={selectedUserId}
            onChange={(e) => setSelectedUserId(e.target.value)}
            className="h-9 w-full rounded-lg border border-input bg-transparent px-3 text-sm text-foreground"
          >
            <option value="">-- Wybierz --</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.first_name} {user.last_name}
              </option>
            ))}
          </select>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Anuluj
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || !selectedUserId}
          >
            {isSubmitting ? "Zapisywanie..." : "Zastosuj"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
